import { useMemo, useState } from "react";
import styles from "./index.module.css";
import Loader from "shared/Loader";
import Link from "shared/Link";

export default function TransactionIssues({
  issues = [],
  isLoading,
  isRefetching,
  error,
  resolveTransactionIssue,
  refetch,
  iboSearch,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  const [submittingAction, setSubmittingAction] = useState(null);

  const normalizedIssues = useMemo(() => {
    const normalizedSearch = String(iboSearch || "").trim();

    return (issues || [])
      .map((issue) => ({
        ...issue,
      }))
      .filter((issue) => {
        if (!normalizedSearch) return true;
        return String(issue.ibo || "").includes(normalizedSearch);
      });
  }, [issues, iboSearch]);

  const toggleExpanded = (transactionId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [transactionId]: !prev[transactionId],
    }));
  };

  const handleResolve = async (issue, actionCode, iboNumber) => {
    if (!actionCode || submittingAction) return;

    setSubmittingAction({
      transactionId: issue.transactionId,
      actionCode,
      iboNumber,
    });

    try {
      await resolveTransactionIssue.mutateAsync({
        transactionId: issue.transactionId,
        paymentId: issue.paymentId,
        actionCode,
      });
    } finally {
      setSubmittingAction(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.loader}>
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.stateMessageError}>
        {error.message || "Failed to load transaction issues"}
      </div>
    );
  }

  if (!normalizedIssues.length) {
    return (
      <div className={styles.stateMessage}>No transaction issues found.</div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.topRow}>
        <div className={styles.infoItem}>
          <div className={styles.infoValue}>
            Total: {normalizedIssues.length}
          </div>
        </div>

        <div className={`${styles.infoItem} ${styles.refreshItem}`}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={refetch}
            disabled={resolveTransactionIssue.isLoading}
          >
            {isRefetching ? <Loader size={20} color="#ffffff" /> : "Refresh"}
          </button>
        </div>
      </div>

      <div className={styles.dividerWrapper}>
        <div className={styles.divider} />
      </div>

      <div className={styles.headerRow}>
        <div>Transaction</div>
        <div>Status</div>
        <div>Glacier</div>
        <div>IBO / Name</div>
        <div>Payment</div>
        <div>Tickets</div>
        <div></div>
      </div>

      <div className={styles.issueList}>
        {normalizedIssues.map((issue, index) => {
          const isExpanded = Boolean(expandedRows[issue.transactionId]);
          const isSubmittingCurrentAction =
            submittingAction?.transactionId === issue.transactionId;

          const hasAction1 = Number(issue.action1) > 0 && issue.action1text;
          const hasAction2 = Number(issue.action2) > 0 && issue.action2text;

          return (
            <div key={issue.transactionId} className={`${styles.issueCard}`}>
              <div className={styles.issueRow}>
                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.transactionId}
                  </div>
                  <div className={styles.secondaryValue}>
                    {issue.transactionDate}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.statusValue}>
                    {issue.transactionStatus || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    {issue.glacierTransactionID || "--"}
                  </div>
                  <div className={styles.secondaryValue}>
                    Status: {issue.glacierSuccess || "--"}
                  </div>
                  <div className={styles.secondaryValue}>
                    Response: {issue.paymentResponse || "--"}
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>{issue.name}</div>
                  <div className={styles.secondaryValue}>
                    <Link
                      to={`/events/orders/ibo/${issue.ibo}`}
                      onClick={() => {}}
                    >
                      IBO: {issue.ibo}
                    </Link>
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    ID: {issue.paymentId}
                  </div>
                  <div className={styles.secondaryValue}>
                    ${Number(issue.paymentSubtotal || 0).toFixed(2)} + $
                    {Number(issue.paymentTax || 0).toFixed(2)} tax
                  </div>
                </div>

                <div className={styles.issueMain}>
                  <div className={styles.primaryValue}>
                    Total: {issue.tickets ?? 0}
                  </div>
                  <div className={styles.secondaryValue}>
                    {[
                      { label: "In Cart", value: issue.inCart },
                      { label: "Pending", value: issue.pending },
                      { label: "Purchased", value: issue.purchased },
                      { label: "Refunded", value: issue.refunded },
                      { label: "On Hold", value: issue.onHold },
                    ]
                      .filter((item) => Number(item.value) > 0)
                      .map((item) => (
                        <div key={item.label}>
                          {item.label}: {item.value}
                        </div>
                      ))}
                  </div>
                </div>

                <div className={styles.issueActions}>
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={() => toggleExpanded(issue.transactionId)}
                  >
                    {isExpanded ? "Hide Tickets" : "View Tickets"}
                  </button>

                  {hasAction1 && (
                    <button
                      type="button"
                      className={styles.actionPrimary}
                      disabled={Boolean(submittingAction)}
                      onClick={() =>
                        handleResolve(issue, issue.action1, issue.ibo)
                      }
                    >
                      {isSubmittingCurrentAction &&
                      submittingAction?.actionCode === issue.action1 ? (
                        <Loader size={20} color="#ffffff" />
                      ) : (
                        issue.action1text
                      )}
                    </button>
                  )}

                  {hasAction2 && (
                    <button
                      type="button"
                      className={styles.actionSecondary}
                      disabled={Boolean(submittingAction)}
                      onClick={() =>
                        handleResolve(issue, issue.action2, issue.ibo)
                      }
                    >
                      {isSubmittingCurrentAction &&
                      submittingAction?.actionCode === issue.action2 ? (
                        <Loader size={20} color="#ffffff" />
                      ) : (
                        issue.action2text
                      )}
                    </button>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className={styles.ticketSection}>
                  <div className={styles.ticketHeaderRow}>
                    <div>Ticket ID</div>
                    <div>Status</div>
                    <div>Attendee</div>
                    <div>Event</div>
                    <div>Ticket</div>
                    <div>Amount</div>

                    <div>Transaction ID</div>
                    <div>Transaction Status</div>
                  </div>

                  <div className={styles.ticketTable}>
                    {issue.transactionTickets.map((ticket, ticketIndex) => (
                      <div
                        key={`${ticket.ticketId}-${ticketIndex}-${ticket.rn}`}
                        className={`${styles.ticketRow} ${
                          ticketIndex % 2 === 0 ? styles.ticketRowAlt : ""
                        }`}
                      >
                        <div>{ticket.ticketId}</div>
                        <div>{ticket.ticketStatus || "--"}</div>
                        <div>{ticket.user}</div>
                        <div>{ticket.event}</div>
                        <div>{ticket.ticket}</div>
                        <div>${Number(ticket.amount || 0).toFixed(2)}</div>

                        <div>{ticket.transactionId || "--"} </div>
                        <div>{ticket.transactionStatus || "--"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
